import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowLeft,
  CalendarX,
  Check,
  Clock,
  CreditCard,
  ExternalLink,
  RefreshCw,
  XCircle,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import useAuthStore from '@/store/useAuthStore'
import useGetBillingSubscription, {
  type BillingInterval,
  type PlanFeature,
  type SubscriptionDetails,
} from '@/services/api/billing/useGetBillingSubscription'
import useGetPlans from '@/services/api/billing/useGetPlans'
import { invoicesQueryKey } from '@/services/api/billing/useGetInvoices'
import {
  type PortalAction,
  useCreateCheckoutSession,
  useCreatePortalSession,
  useResumeSubscription,
} from '@/services/api/billing/useBillingSessions'
import PlanPicker from '@/components/billing/PlanPicker'
import InvoiceTable from '@/components/billing/InvoiceTable'
import EndedSubscriptionNotice from '@/components/billing/EndedSubscriptionNotice'
import { billingBackTarget } from '@/components/billing/billingBackTarget'
import {
  PORTAL_DONE_MESSAGE,
  portalChangeApplied,
  rememberPortalAction,
  takePortalExpectation,
  type PortalExpectation,
} from '@/components/billing/portalReturn'
import {
  formatBillingDate,
  formatMoney,
  INTERVAL_ADJECTIVE,
  INTERVAL_LABEL,
  wasToastedByInterceptor,
} from '@/components/billing/format'

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Payment due',
  unpaid: 'Unpaid',
  canceled: 'Cancelled',
}

/** Webhooks land a moment after Stripe redirects back — poll until they do. */
const RETURN_POLL_MS = 2000
const RETURN_POLL_ATTEMPTS = 8

export default function SubscriptionPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const currentTeamId = useAuthStore((s) => s.currentTeamId)

  const { data, isLoading, refetchForce } = useGetBillingSubscription({
    teamId: currentTeamId,
  })
  const checkout = useCreateCheckoutSession()
  const portal = useCreatePortalSession()
  const resume = useResumeSubscription()

  const hasActive = !!data?.has_active_subscription
  const isOwner = !!data?.is_billing_owner
  const sub = data?.subscription ?? null
  const [changingPlan, setChangingPlan] = useState(false)
  const [awaitingWebhook, setAwaitingWebhook] = useState(false)
  // Back from the portal: wait until Stripe has applied the change.
  const [portalPending, setPortalPending] = useState<PortalExpectation | null>(
    null,
  )
  const [portalRefreshed, setPortalRefreshed] = useState(false)
  const returnHandled = useRef(false)

  const showPicker = isOwner && (!hasActive || changingPlan)
  const { data: plans, isLoading: plansLoading } = useGetPlans({
    enabled: !isLoading && showPicker,
  })

  // Return from Stripe Checkout (?status=) or a portal flow (?portal=done).
  useEffect(() => {
    if (returnHandled.current) return
    const params = new URLSearchParams(location.search)
    const status = params.get('status')
    const portalDone = params.get('portal') === 'done'
    if (!status && !portalDone) return
    returnHandled.current = true

    if (status === 'success') {
      toast.success('Thanks! Your subscription is being activated.')
      setAwaitingWebhook(true)
    } else if (status === 'canceled') {
      toast.info('Checkout cancelled — you have not been charged.')
    } else if (portalDone) {
      // No remembered action (e.g. storage blocked): one refresh is enough.
      setPortalPending(
        takePortalExpectation(currentTeamId) ?? {
          teamId: String(currentTeamId),
          action: 'payment_method',
          startedAt: Date.now(),
        },
      )
    }
    navigate(location.pathname, { replace: true })
  }, [location, navigate, queryClient, refetchForce, currentTeamId])

  // After checkout: refetch until the subscription shows up (or give up).
  useEffect(() => {
    if (!awaitingWebhook) return
    if (hasActive) {
      setAwaitingWebhook(false)
      queryClient.invalidateQueries({
        queryKey: invoicesQueryKey(currentTeamId),
      })
      return
    }
    let attempts = 0
    refetchForce()
    const timer = window.setInterval(() => {
      attempts += 1
      if (attempts >= RETURN_POLL_ATTEMPTS) {
        window.clearInterval(timer)
        setAwaitingWebhook(false)
        toast.info(
          'Your payment went through — the plan may take a minute to appear. Refresh to check.',
        )
        return
      }
      refetchForce()
    }, RETURN_POLL_MS)
    return () => window.clearInterval(timer)
  }, [awaitingWebhook, hasActive, refetchForce, queryClient, currentTeamId])

  // After a portal flow: pull from Stripe until the change shows (the
  // server syncs on every forced refresh), then confirm; give up gracefully.
  useEffect(() => {
    if (!portalPending) return
    let attempts = 0
    let active = true
    const pull = () =>
      refetchForce().finally(() => active && setPortalRefreshed(true))

    pull()
    const timer = window.setInterval(() => {
      attempts += 1
      if (attempts >= RETURN_POLL_ATTEMPTS) {
        window.clearInterval(timer)
        setPortalPending(null)
        setPortalRefreshed(false)
        toast.info(
          'Stripe is still processing your change — it will show here in a minute. Refresh to check.',
        )
        return
      }
      pull()
    }, RETURN_POLL_MS)

    return () => {
      active = false
      window.clearInterval(timer)
    }
  }, [portalPending, refetchForce])

  useEffect(() => {
    if (!portalPending || !portalRefreshed) return
    if (!portalChangeApplied(portalPending, sub)) return

    toast.success(PORTAL_DONE_MESSAGE[portalPending.action])
    queryClient.invalidateQueries({ queryKey: invoicesQueryKey(currentTeamId) })
    setPortalPending(null)
    setPortalRefreshed(false)
  }, [portalPending, portalRefreshed, sub, queryClient, currentTeamId])

  const startCheckout = async (planKey: string, interval: BillingInterval) => {
    try {
      const { url } = await checkout.mutateAsync({
        teamId: currentTeamId,
        planKey,
        interval,
      })
      window.location.href = url
    } catch (error) {
      if (!wasToastedByInterceptor(error))
        toast.error('Could not start checkout')
    }
  }

  const openPortal = async (
    action: PortalAction,
    target?: { planKey: string; interval: BillingInterval },
  ) => {
    try {
      const { url } = await portal.mutateAsync({
        teamId: currentTeamId,
        action,
        planKey: target?.planKey,
        interval: target?.interval,
      })
      rememberPortalAction({
        teamId: String(currentTeamId),
        action,
        planKey: target?.planKey,
        interval: target?.interval,
      })
      window.location.href = url
    } catch (error) {
      if (!wasToastedByInterceptor(error)) toast.error('Could not open Stripe')
    }
  }

  const resumeSubscription = async () => {
    try {
      await resume.mutateAsync(currentTeamId)
      await refetchForce()
      toast.success('Your subscription will continue.')
    } catch (error) {
      if (!wasToastedByInterceptor(error))
        toast.error('Could not resume the subscription')
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate(billingBackTarget())}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back
      </button>

      <h1 className="mb-1 text-2xl font-semibold">Billing</h1>
      <p className="mb-6 text-muted-foreground">
        Plan, payment and invoices for this space.
      </p>

      {isLoading ? (
        <Card className="w-full space-y-3 p-6">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-9 w-48" />
        </Card>
      ) : awaitingWebhook || portalPending ? (
        <Card className="flex w-full items-center gap-3 p-6">
          <RefreshCw className="size-5 animate-spin text-primary" />
          <p>
            {awaitingWebhook
              ? 'Activating your subscription…'
              : 'Updating your subscription…'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {hasActive && sub && (
            <>
              <StatusBanners
                sub={sub}
                isOwner={isOwner}
                busy={portal.isPending}
                onAddCard={() => openPortal('payment_method')}
              />
              <CurrentPlanCard
                sub={sub}
                isOwner={isOwner}
                busy={portal.isPending || resume.isPending}
                changingPlan={changingPlan}
                onChangePlan={() => setChangingPlan((v) => !v)}
                onPaymentMethod={() => openPortal('payment_method')}
                onCancel={() => openPortal('cancel')}
                onResume={resumeSubscription}
              />
            </>
          )}

          {!hasActive && data?.ended_subscription && (
            <EndedSubscriptionNotice
              wasTrial={data.ended_subscription.was_trial}
              isOwner={isOwner}
            />
          )}

          {!isOwner && !hasActive && !data?.ended_subscription && (
            <Card className="w-full p-6 text-sm text-muted-foreground">
              This space doesn&apos;t have an active subscription. Only the
              space owner can choose a plan — contact them to restore access.
            </Card>
          )}

          {showPicker && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">
                {hasActive ? 'Change plan' : 'Choose a plan'}
              </h2>
              {plansLoading ? (
                <Card className="w-full p-6">
                  <Skeleton className="h-24 w-full" />
                </Card>
              ) : !plans?.length ? (
                <Card className="w-full p-6 text-sm text-muted-foreground">
                  No plans are available right now.
                </Card>
              ) : hasActive ? (
                <PlanPicker
                  plans={plans}
                  current={{
                    planKey: sub?.plan_key ?? null,
                    interval: sub?.interval ?? null,
                  }}
                  actionLabel="Switch to this plan"
                  busy={portal.isPending}
                  onChoose={(planKey, interval) =>
                    openPortal('update', { planKey, interval })
                  }
                />
              ) : (
                <PlanPicker
                  plans={plans}
                  actionLabel="Subscribe"
                  busy={checkout.isPending}
                  onChoose={startCheckout}
                />
              )}
              <p className="text-xs text-muted-foreground">
                {hasActive
                  ? "You'll confirm the change and any proration on Stripe's secure page."
                  : "You'll be taken to Stripe's secure checkout. Promotion codes can be entered there."}
              </p>
            </section>
          )}

          {isOwner && <InvoiceTable teamId={currentTeamId} />}

          {hasActive && <PlanFeatureList features={data?.features ?? []} />}
        </div>
      )}
    </div>
  )
}

function StatusBanners({
  sub,
  isOwner,
  busy,
  onAddCard,
}: {
  sub: SubscriptionDetails
  isOwner: boolean
  busy: boolean
  onAddCard: () => void
}) {
  if (sub.payment_issue) {
    return (
      <Card className="flex w-full flex-wrap items-center gap-3 border-destructive/40 bg-destructive/5 p-4">
        <AlertTriangle className="size-5 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">Payment failed</p>
          <p className="text-sm text-muted-foreground">
            {isOwner
              ? "We'll retry automatically. Pay now or update your card to avoid losing access."
              : 'Ask the space owner to update the payment details.'}
          </p>
        </div>
        {isOwner && (
          <div className="flex gap-2">
            {sub.pay_invoice_url && (
              <a
                href={sub.pay_invoice_url}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ size: 'sm' })}
              >
                Pay now
              </a>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={onAddCard}
              disabled={busy}
            >
              Update card
            </Button>
          </div>
        )}
      </Card>
    )
  }

  if (sub.is_trialing && sub.trial_days_left !== null && !sub.cancel_at) {
    const days = sub.trial_days_left
    return (
      <Card className="flex w-full flex-wrap items-center gap-3 border-primary/30 bg-primary/5 p-4">
        <Clock className="size-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            {days <= 0
              ? 'Your trial ends today'
              : `${days} day${days === 1 ? '' : 's'} left in your trial`}
          </p>
          <p className="text-sm text-muted-foreground">
            {isOwner ? (
              <>
                Add a payment method to keep access when the trial ends on{' '}
                {formatBillingDate(sub.trial_ends_at)}. You won&apos;t be
                charged before then.
              </>
            ) : (
              <>
                The space owner needs to add a payment method before the trial
                ends on {formatBillingDate(sub.trial_ends_at)}.
              </>
            )}
          </p>
        </div>
        {isOwner && (
          <Button size="sm" onClick={onAddCard} disabled={busy}>
            Add payment method
          </Button>
        )}
      </Card>
    )
  }

  return null
}

function CurrentPlanCard({
  sub,
  isOwner,
  busy,
  changingPlan,
  onChangePlan,
  onPaymentMethod,
  onCancel,
  onResume,
}: {
  sub: SubscriptionDetails
  isOwner: boolean
  busy: boolean
  changingPlan: boolean
  onChangePlan: () => void
  onPaymentMethod: () => void
  onCancel: () => void
  onResume: () => void
}) {
  const cancelling = !!sub.cancel_at

  return (
    <Card className="w-full p-6">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">Current plan</p>
          <p className="text-lg font-semibold">{sub.plan_name}</p>
          {sub.amount != null && sub.currency && sub.interval && (
            <p className="text-sm text-muted-foreground">
              {formatMoney(sub.amount, sub.currency)} /{' '}
              {INTERVAL_LABEL[sub.interval]}
              {' · '}
              {INTERVAL_ADJECTIVE[sub.interval]} billing
            </p>
          )}
        </div>
        <span
          className={`shrink-0 rounded px-2 py-1 text-xs font-medium ${
            cancelling
              ? 'bg-gray-100 text-gray-700'
              : sub.status === 'active'
                ? 'bg-green-100 text-green-800'
                : sub.status === 'trialing'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {cancelling ? 'Cancelling' : STATUS_LABEL[sub.status] ?? sub.status}
        </span>
      </div>

      {cancelling ? (
        <p className="mb-4 flex items-center gap-2 text-sm">
          <CalendarX className="size-4 text-muted-foreground" />
          Access ends on {formatBillingDate(sub.cancel_at)}. You won&apos;t be
          charged again.
        </p>
      ) : (
        <p className="mb-4 text-sm text-muted-foreground">
          {sub.is_trialing
            ? `Trial ends ${formatBillingDate(sub.trial_ends_at)}`
            : `Next payment ${formatBillingDate(sub.next_billing_date)}`}
        </p>
      )}

      {isOwner ? (
        <>
          <Separator className="mb-4" />
          <div className="flex flex-wrap gap-2">
            {cancelling ? (
              <Button onClick={onResume} disabled={busy}>
                <RefreshCw className="mr-2 size-4" />
                Keep my subscription
              </Button>
            ) : (
              <Button
                variant={changingPlan ? 'secondary' : 'default'}
                onClick={onChangePlan}
              >
                {changingPlan ? 'Hide plans' : 'Change plan'}
              </Button>
            )}
            <Button variant="outline" onClick={onPaymentMethod} disabled={busy}>
              <CreditCard className="mr-2 size-4" />
              Payment method
              <ExternalLink className="ml-1.5 size-3 opacity-60" />
            </Button>
            {!cancelling && (
              <Button
                variant="destructiveGhost"
                onClick={onCancel}
                disabled={busy}
              >
                <XCircle className="mr-2 size-4" />
                Cancel subscription
              </Button>
            )}
          </div>
        </>
      ) : (
        <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
          Only the space owner can change the plan or payment details.
        </p>
      )}
    </Card>
  )
}

function PlanFeatureList({ features }: { features: PlanFeature[] }) {
  const visible = features.filter((f) => f.fe_visibility !== 'hide')
  if (visible.length === 0) return null

  return (
    <Card className="w-full p-6">
      <h3 className="mb-3 font-medium">Features</h3>
      <Separator className="mb-3" />
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {visible.map((f) => (
          <li
            key={f.lookup_key}
            className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${
              f.available ? '' : 'opacity-50'
            }`}
          >
            <span className="flex items-center gap-1.5">
              {f.available && <Check className="size-3.5 text-green-600" />}
              {f.name}
            </span>
            <span className="text-muted-foreground">
              {f.available ? f.value ?? '' : 'Locked'}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}
